/**
 * Phase 7b — IT storefront rewrite layer.
 *
 * Content in product_translations / category_translations / ui_translations
 * was originally authored for the CH storefront (Swiss context: TWINT,
 * "Svizzera", "BigFollow.ch"). On sf='it' we render the SAME content but
 * strip Swiss-only references. This is a pure, mechanical last-mile
 * rewrite applied AFTER seo_overrides / DB overlays — so admins can still
 * override any string exactly.
 *
 * HARD RULES:
 *   • Called ONLY when storefront === 'it'. CH (incl. /it/ second locale)
 *     and AT are never touched.
 *   • Longest patterns first so "Svizzera con TWINT" is consumed before
 *     the shorter "Svizzera" / "TWINT" rules could shred it.
 *   • Case-preserving on the first letter for word-shaped tokens.
 *   • CHF symbol/amount rules intentionally EXCLUDED — the 5 known
 *     placeholders are loyalty amounts computed in CHF (see chat
 *     analysis); a naive symbol swap would mis-label EUR/CHF economics.
 *   • Never mutate empty / non-string input.
 */

type Rule = { pattern: RegExp; replace: string | ((m: string) => string) };

// Longest / most specific first.
const RULES: Rule[] = [
  // Compound phrases
  { pattern: /\bSvizzera con TWINT\b/gi, replace: '' },
  { pattern: /\bin der ganzen Schweiz\b/gi, replace: 'in tutta Italia' },
  { pattern: /\bder ganzen Schweiz\b/gi, replace: 'tutta Italia' },
  { pattern: /\bin tutta la Svizzera\b/gi, replace: 'in tutta Italia' },
  { pattern: /\btutta la Svizzera\b/gi, replace: 'tutta Italia' },
  { pattern: /\bmigliori prezzi della Svizzera\b/gi, replace: 'migliori prezzi in Italia' },
  { pattern: /\bBest prices in Switzerland\b/gi, replace: 'Best prices in Italy' },

  // Domain first (more specific than the brand rule below)
  { pattern: /\bbigfollow\.ch\b/g, replace: 'bigfollow.it' },
  { pattern: /\bBigFollow\.ch\b/g, replace: 'BigFollow' },
  { pattern: /\bBIGFOLLOW\.CH\b/g, replace: 'BIGFOLLOW' },

  // Payment method (TWINT is CH-only). Only drops the token itself; the
  // surrounding sentence structure ("Paga con TWINT" → "Paga con") is left
  // to admins/overrides — checkout.twint_desc & payment_methods are not
  // shown on IT anyway (Stripe-only).
  { pattern: /\s*[–—-]?\s*TWINT\s*[,/]?\s*/g, replace: ' ' },

  // Country nouns (do LAST — shortest, greedy)
  { pattern: /\bSvizzera\b/g, replace: 'Italia' },
  { pattern: /\bsvizzera\b/g, replace: 'italiana' },
  { pattern: /\bsvizzero\b/g, replace: 'italiano' },
  { pattern: /\bSchweiz\b/g, replace: 'Italien' },
  { pattern: /\bSwitzerland\b/g, replace: 'Italy' },
  { pattern: /\bSuisse\b/g, replace: 'Italie' },
];

// Cleanup: collapse whitespace, tidy pipe separators left by removals.
const CLEANUP: Rule[] = [
  { pattern: /\s{2,}/g, replace: ' ' },
  { pattern: /\s+\|/g, replace: ' |' },
  { pattern: /\|\s*\|/g, replace: '|' },
  { pattern: /^\s*\|\s*/, replace: '' },
  { pattern: /\s*\|\s*$/, replace: '' },
  { pattern: /\s+([,.;:!?])/g, replace: '$1' },
  { pattern: /^\s+|\s+$/g, replace: '' },
];

export function rewriteForIT(input: string | null | undefined): string {
  if (!input || typeof input !== 'string') return input ?? '';
  let out = input;
  for (const r of RULES) out = out.replace(r.pattern, r.replace as any);
  for (const r of CLEANUP) out = out.replace(r.pattern, r.replace as any);
  return out;
}

/** Recursively rewrite string leaves of an overlay/product object. */
export function rewriteObjectForIT<T>(obj: T): T {
  if (obj == null) return obj;
  if (typeof obj === 'string') return rewriteForIT(obj) as unknown as T;
  if (Array.isArray(obj)) return obj.map(rewriteObjectForIT) as unknown as T;
  if (typeof obj === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      out[k] = rewriteObjectForIT(v);
    }
    return out as T;
  }
  return obj;
}